import React, { useEffect, useState } from "react";
import { Form, Col, Row, Typography, Button, Image, Upload, message, Input, Spin, Collapse, Badge, notification, Card } from "antd";
import { EditOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import type { UploadChangeParam } from "antd/es/upload";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { UpdateUserInfo, getUserInfo } from "./user-info.reducer";
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { translations } from '@/i18n/i18n';
import { useGetCurrentUserCompanyQuery, useGetUserInfoQuery, userInfoApi, useUpdateUserInfoMutation, useUploadAvatarMutation } from "@/services/api/user-info.api";

import './index.css'
import { Navigate, useNavigate } from "react-router-dom";
import useIsMobile from "@/shared/utils/use-is-mobile.hook";
import { Descriptions } from 'antd';
import type { DescriptionsProps } from 'antd';
import { Footer } from "antd/es/layout/layout";
import { useGetCurrentCompanyQuery } from "@/services/api/company.api";
import { fileApi } from "@/services/api/file.api";

const { Title, Text } = Typography;

const getBase64 = (img: RcFile, callback: (url: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(img);
};

const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
        notification.error({
            message:
                (
                    <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                        Bạn chỉ có thể tải định dạng JPG/PNG!
                    </div>
                )
        });
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
        notification.error({
            message:
                (
                    <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                        Ảnh phải ít hơn 2MB!
                    </div>
                )
        });
    }
    return isJpgOrPng && isLt2M;
};

const schema = yup.object().shape({
    phoneNumber: yup
        .number()
        .required("Hãy nhập số điện thoại!")
        .typeError('Số điện thoại không hợp lệ!')
        .test('isPhoneNumber', 'Số điện thoại phải chứa 10 số!', (value: any) => {
            return /^\d{9}$/.test(value);
        }),
    email: yup
        .string()
        .required("Hãy nhập email!")
        .test('', 'Email không hợp lệ!', (values: any) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(values);
        }),
    age: yup
        .number()
        .required()
        .typeError('Chỉ được nhập số!')
        .positive()
        .integer()
        .round()
        .min(6)
        .max(12),

    firstName: yup.string().required(),
    lastName: yup.string().required(),
    fullName: yup.string().required("Hãy nhập họ và tên!"),
    employeeCode: yup.string().required(),
    roleAssignmentText: yup.string().required(),
    position: yup.string().required(),
    department: yup.string().required(),
});

export const yupSync: any = {
    async validator({ field }: { field: string }, value: any) {
        await schema.validateSyncAt(field, { [field]: value });
    },
    type: 'custom',
};

function hasVietnameseCharacters(path: any): boolean {
    const vietnameseRegex = /[áàảãạâấầẩẫậăắằẳẵặéèẻẽẹêếềểễệíìỉĩịóòỏõọôốồổỗộơớờởỡợúùủũụưứừửữựýỳỷỹỵđ]/i;
    return vietnameseRegex.test(path);
}

export default function UserInfoPage() {
    const { t } = useTranslation();
    const isMobile = useIsMobile()
    const dispatch = useAppDispatch();
    const { data: useInfo, isLoading: isLoadGet, isError: isErrorGet, isSuccess: isSuccessGet, refetch } = useGetCurrentUserCompanyQuery();// useGetUserInfoQuery()
    const [downloadAvatar, { data: avatar, isLoading: loadingAvatar, isError: errorGetAvatar, isSuccess: successGetAvatar }] = fileApi.endpoints.downloadFile.useLazyQuery();
    const [updateUserInfo, { isError: isErrorUpdate, isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate }] = useUpdateUserInfoMutation()
    const [uploadAvatarFile, { data: upLoadAvatar, isError: uploadAvatarError, isLoading: uploadAvatarLoading, isSuccess: uploadAvatarSuccess }] = useUploadAvatarMutation()
    const [edit, setEdit] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [avatarEdit, setAvatarEdit] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>(`${useInfo?.avatar}`);
    const [avatarFileRef, setAvatarFileRef] = useState<any>()
    const [firstTimeRender, setFirstTimeRender] = useState(true)
    const [avatarUserFinal, setAvatarUserFinal] = useState<any>();
    const validImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const {
        data: companyEntity,
        isLoading: isLoadingCompany,
        isError: isErrorCompany,
        isSuccess: isSuccessCompany,
    } = useGetCurrentCompanyQuery();

    const [clickUploadImage, setClickUploadImage] = useState(false)

    const navigate = useNavigate()

    const handleChange: UploadProps["onChange"] = (
        info: UploadChangeParam<UploadFile>
    ) => {
        if (info.file.status === "uploading") {
            setLoading(true);
            return;
        }
        if (info.file.status === "done") {
            // Get this url from response in real world.
            getBase64(info.file.originFileObj as RcFile, (url) => {
                setLoading(false);
                setImageUrl(url);
            });
        }
    };

    const uploadAvatar: UploadProps = {
        // onRemove: file => { },
        beforeUpload: file => {
            const isImage = validImageTypes.includes(file.type);
            const maxSizeMB = 5;

            if (!isImage) {
                notification.error({
                    message:
                        (
                            <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                                {`file ${file.name} không hợp lệ`}
                            </div>
                        )
                })
                return Upload.LIST_IGNORE;
            }

            if (file.size / 1024 / 1024 > maxSizeMB) {
                notification.error({
                    message:
                        (
                            <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                                {`file chỉ chứa tối đa ${maxSizeMB}MB`}
                            </div>
                        )
                })
                return Upload.LIST_IGNORE;
            }
            const formData = new FormData();
            formData.append('file', file);
            uploadAvatarFile(formData)
            return false;
        },
    };


    const onSave = async () => {
        if (edit) {
            try {
                const values = await form.validateFields();

                const updatedValues = {
                    ...values,
                    id: useInfo?.id,
                    avatar: avatarFileRef
                };

                await updateUserInfo(updatedValues);
                setAvatarUserFinal(avatarEdit)
                refetch()
            } catch (error) {
                notification.error({
                    message:
                        (
                            <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                                Lưu dữ liệu không thành công!
                            </div>
                        )
                })
            }
        }

    };

    const onEdit = () => {
        // navigate(`/admin/userinfo/changeInfo/${useInfo?.id}`)
        setEdit(true)
    }

    const onCancel = () => {
        // setAvatarEdit('')
        console.log(avatarEdit)
        setEdit(false)
    }

    useEffect(() => {
        if (useInfo?.avatar) {
            downloadAvatar({ fileId: useInfo.avatar, objectType: !edit ? "user" : null });
        }
    }, [isSuccessGet])

    useEffect(() => {
        if (edit) {
            form.setFieldsValue(useInfo);
        }
    }, [edit])


    useEffect(() => {
        if (isSuccessUpdate) {
            console.log(useInfo)
            notification.success({
                message:
                    (
                        <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                            Cập nhật thông tin thành công!
                        </div>
                    )
            })
            setEdit(false)
        }
        if (isErrorUpdate) {
            notification.error({
                message:
                    (
                        <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                            Cập nhật thông tin không thành công!
                        </div>
                    )
            })
            setEdit(false)
        }
    }, [isSuccessUpdate, isErrorUpdate, isLoadingUpdate])

    useEffect(() => {
        if (uploadAvatarSuccess) {
            if (hasVietnameseCharacters(upLoadAvatar?.name)) {
                notification.info({
                    message:
                        (
                            <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                                File ảnh không hợp lệ
                            </div>
                        )
                })
            }
            else {
                console.log("upLoadAvatar?.fileRef-----------------", upLoadAvatar?.fileRef || "")
                setAvatarFileRef(upLoadAvatar?.fileRef)
                downloadAvatar({ fileId: upLoadAvatar?.fileRef || "", objectType: !edit ? "user" : null });
                // setAvatarFile(!!upLoadAvatar?.fileRef ? upLoadAvatar?.fileRef : `${useInfo?.avatar}`)
            }
        }
        // else if (uploadAvatarError) {
        //     notification.error({ message: "Tải ảnh đại diện không thành công!" })
        //     // setEdit(true)
        // }

    }, [uploadAvatarSuccess, uploadAvatarLoading, uploadAvatarError])

    useEffect(() => {
        // if (errorGetAvatar) {
        //     notification.error({ message: "Tải ảnh đại diện không thành công!" })
        // }

        if (successGetAvatar) {
            if (clickUploadImage) {
                notification.success({
                    message:
                        (
                            <div style={{ fontWeight: 600, fontFamily: 'Lexend' }}>
                                Tải ảnh đại diện thành công!
                            </div>
                        )
                })
                setClickUploadImage(false)
            }
            setAvatarEdit(avatar)
            if (firstTimeRender) {
                setAvatarUserFinal(avatar)
                setFirstTimeRender(false)
            }
        }
    }, [successGetAvatar, errorGetAvatar, loadingAvatar, avatar])

    return (
        <Spin spinning={isLoadingUpdate}>
            <div
                className="userInfo"
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "white",
                    padding: "1% 3% 1% 3%",
                    boxShadow: "2px 2px 10px rgba(0, 0, 0, 0.2)",
                    height: '100%'
                }}
            >
                <Col
                    lg={{ span: 24 }}
                    xs={{ span: 24 }}
                >
                    <Row>
                        <Title level={3}>{useInfo?.fullName}</Title>
                    </Row>
                    <Row gutter={[24, 0]} style={{ marginBottom: "3%" }} align={'top'} justify={'space-between'}>
                        <Col lg={{ span: 5 }} xs={{ span: 24 }} style={{ padding: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: isMobile ? 'auto' : '100%' }}>
                                {
                                    !edit ? (
                                        loadingAvatar
                                            ? <Spin indicator={<LoadingOutlined style={{ fontSize: 124 }} spin />} />
                                            : (
                                                <Image
                                                    style={{
                                                        border: "1px solid #e0e0e0",
                                                        borderRadius: 8,
                                                        width: '100%',
                                                        maxHeight: "25vw",
                                                    }}
                                                    src={avatarUserFinal}
                                                    fallback="data:image/png;base64,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"
                                                ></Image>
                                            )

                                    ) : (
                                        loadingAvatar ? <Spin indicator={<LoadingOutlined style={{ fontSize: 124 }} spin />} />
                                            : (
                                                <div style={{ textAlign: 'center', width: '100%' }}>
                                                    <Image
                                                        style={{
                                                            border: "1px solid #e0e0e0",
                                                            borderRadius: 8,
                                                            width: '100%',
                                                            maxHeight: "25vw",
                                                            marginBottom: 20
                                                        }}
                                                        src={avatarEdit}
                                                        preview={false}
                                                        fallback="data:image/png;base64,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"
                                                    ></Image>
                                                    <Upload
                                                        name={'avatar'}
                                                        {...uploadAvatar}
                                                        className="avatar-uploader"
                                                        showUploadList={false}
                                                        // action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
                                                        style={{ width: '100%' }}
                                                    // onChange={handleChange}
                                                    >
                                                        <Button onClick={() => { setClickUploadImage(true) }} style={{ width: '100%', height: 40, padding: '0 6vw', backgroundColor: 'rgb(232, 241, 254)', color: 'rgb(24, 119, 242)', border: 'none' }}>
                                                            Tải ảnh lên
                                                        </Button>
                                                    </Upload>
                                                </div>
                                            )
                                    )
                                }


                            </div>
                        </Col>

                        <Col lg={{ span: 18 }} xs={{ span: 24 }}>
                            <Row style={{ width: '100%', marginBottom: 20 }}>
                                <Card type='inner' title={'Thông tin cá nhân'} style={{ margin: '0 20px', width: '100%' }}>
                                    <Form colon={false}
                                        form={form}
                                        labelCol={{ span: 6 }}
                                        layout="horizontal"
                                        initialValues={useInfo}
                                    // initialValues={digitalSign}
                                    >
                                        <Form.Item
                                            label={t(translations.user.status)}
                                            labelAlign="left"
                                            style={{
                                                borderBottom: '1px solid #F0F0F0',
                                                padding: 10,
                                                margin: 0
                                            }}
                                        >

                                            <Badge status={useInfo?.status === "ACTIVE" ? "success" : "error"} text={useInfo?.status === "ACTIVE" ? "Hoạt động" : "Ngưng hoạt động"} style={{ color: useInfo?.status === "ACTIVE" ? "green" : "red" }} />
                                            {/* 23kl5jfrr6e78bsmkuiy63495h */}
                                        </Form.Item>
                                        {
                                            !edit ? (
                                                <Form.Item
                                                    name={'fullName'}
                                                    label={t(translations.user.fullName)}
                                                    labelAlign="left"
                                                    style={{
                                                        borderBottom: '1px solid #F0F0F0',
                                                        padding: 10,
                                                        margin: 0
                                                    }}
                                                >
                                                    <div>{useInfo?.fullName}</div>
                                                </Form.Item>
                                            ) : (
                                                <Form.Item
                                                    name={'fullName'}
                                                    label={t(translations.user.fullName)}
                                                    labelAlign="left"
                                                    hasFeedback
                                                    rules={[yupSync]}
                                                    style={{
                                                        borderBottom: '1px solid #F0F0F0',
                                                        padding: 10,
                                                        margin: 0

                                                    }}
                                                >
                                                    <Input style={{ height: 40 }}></Input>
                                                </Form.Item>
                                            )
                                        }

                                        {
                                            !edit ? (
                                                <Form.Item
                                                    name={'phoneNumber'}
                                                    label={t(translations.user.phone)}
                                                    labelAlign="left"
                                                    style={{
                                                        borderBottom: '1px solid #F0F0F0',
                                                        padding: 10,
                                                        margin: 0

                                                    }}
                                                >
                                                    <div>{useInfo?.phoneNumber}</div>
                                                </Form.Item>
                                            ) : (
                                                <Form.Item
                                                    name={'phoneNumber'}
                                                    label={t(translations.user.phone)}
                                                    labelAlign="left"
                                                    rules={[yupSync]}
                                                    hasFeedback
                                                    style={{
                                                        borderBottom: '1px solid #F0F0F0',
                                                        padding: 10,
                                                        margin: 0

                                                    }}
                                                >
                                                    <Input style={{ height: 40 }}></Input>
                                                </Form.Item>
                                            )
                                        }

                                        {
                                            !edit ? (
                                                <Form.Item
                                                    name={'email'}
                                                    label={t(translations.user.email)}
                                                    labelAlign="left"
                                                    style={{
                                                        borderBottom: '1px solid #F0F0F0',
                                                        padding: 10,
                                                        margin: 0

                                                    }}
                                                >
                                                    <div>{useInfo?.email}</div>
                                                </Form.Item>
                                            ) : (
                                                <Form.Item
                                                    name={'email'}
                                                    label={t(translations.user.email)}
                                                    labelAlign="left"
                                                    style={{
                                                        borderBottom: '1px solid #F0F0F0',
                                                        padding: 10,
                                                        margin: 0

                                                    }}
                                                    rules={[yupSync]}
                                                    hasFeedback
                                                >
                                                    <Input style={{ height: 40 }}></Input>
                                                </Form.Item>
                                            )
                                        }


                                        {/* <Form.Item
                                            label={t(translations.user.position)}
                                            labelAlign="left"
                                            style={{
                                                borderBottom: '1px solid #F0F0F0',
                                                padding: 10,
                                                margin:0
                                                
                                            }}
                                        >
                                            <div>{useInfo?.position === "LEGAL_REPRESENTATIVE" ? "Người đại diện pháp luật" :
                                                useInfo?.position === "CHIEF_ACCOUNTANT" ? "Kế toán trưởng" :
                                                    useInfo?.position === "STAFF" ? "Nhân viên" :
                                                        useInfo?.position === "MANAGER" ? "Trưởng phòng" :
                                                            useInfo?.position === "SUB_MANAGER" ? "Phó phòng" :
                                                                null}</div>
                                        </Form.Item>
                                        <Form.Item
                                            label={t(translations.user.department)}
                                            labelAlign="left"
                                            style={{
                                                borderBottom: '1px solid #F0F0F0',
                                                padding: 10,
                                                margin:0
                                                
                                            }}
                                        >
                                            <div>{useInfo?.department?.name}</div>
                                        </Form.Item> */}
                                    </Form>
                                </Card>
                            </Row>

                            <Row>
                                <Card type='inner' title={'Thông tin công ty'} style={{ margin: '0 20px', width: '100%' }}>
                                    <Form colon={false}
                                        labelCol={{ span: 6 }}
                                        layout="horizontal"
                                        labelWrap={true}
                                    >
                                        <Form.Item
                                            label={"Tên tiếng việt"}
                                            labelAlign="left"
                                            style={{
                                                borderBottom: '1px solid #F0F0F0',
                                                padding: 10,
                                                margin: 0
                                            }}
                                        >

                                            {useInfo?.company?.name}
                                        </Form.Item>

                                        <Form.Item
                                            label={"Tên tiếng anh"}
                                            labelAlign="left"
                                            style={{
                                                borderBottom: '1px solid #F0F0F0',
                                                padding: 10,
                                                margin: 0
                                            }}
                                        >

                                            {useInfo?.company?.nameEn}
                                        </Form.Item>


                                        <Form.Item
                                            label={"Địa chỉ"}
                                            labelAlign="left"
                                            style={{
                                                borderBottom: '1px solid #F0F0F0',
                                                padding: 10,
                                                margin: 0
                                            }}
                                        >
                                            {companyEntity?.mainAddress?.address}
                                            {/* {useInfo?.company?.mainAddress?.address} */}
                                        </Form.Item>

                                        {/* <Form.Item
                                        label={"Số fax"}
                                        labelAlign="left"
                                        style={{
                                            borderBottom: '1px solid #F0F0F0',
                                            padding: 10,
                                            margin: 0
                                        }}
                                    >

                                        {useInfo?.company?.fax}
                                    </Form.Item>
                                    <Form.Item
                                        label={"Mã số thuế"}
                                        labelAlign="left"
                                        style={{
                                            borderBottom: '1px solid #F0F0F0',
                                            padding: 10,
                                            margin: 0
                                        }}
                                    >
                                        {useInfo?.company?.code}
                                    </Form.Item>
                                    <Form.Item
                                        label={"Số điện thoại"}
                                        labelAlign="left"
                                        style={{
                                            borderBottom: '1px solid #F0F0F0',
                                            padding: 10,
                                            margin: 0
                                        }}
                                    >
                                        {useInfo?.company?.phoneNumber}
                                    </Form.Item>
                                    <Form.Item
                                        label={"Email"}
                                        labelAlign="left"
                                        style={{
                                            borderBottom: '1px solid #F0F0F0',
                                            padding: 10,
                                            margin: 0
                                        }}
                                    >
                                        {useInfo?.company?.email}
                                    </Form.Item>
                                    <Form.Item
                                        label={"Website"}
                                        labelAlign="left"
                                        style={{
                                            borderBottom: '1px solid #F0F0F0',
                                            padding: 10,
                                            margin: 0
                                        }}
                                    >
                                        {useInfo?.company?.website}
                                    </Form.Item> */}
                                    </Form>
                                </Card>
                            </Row>
                        </Col>
                    </Row>
                    <Row style={{ backgroundColor: 'white' }}>
                        <Col span={24}>
                            <Footer style={{ float: 'right', backgroundColor: 'white', padding: 20 }}>
                                {
                                    !edit ? (<Button onClick={onEdit} style={{ width: '100%', height: 40 }}>
                                        {/* <EditOutlined /> */}
                                        {(t(translations.user.edit))}
                                    </Button>) : (
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>

                                            <Button onClick={onCancel} style={{ marginRight: 20, width: '100%', height: 40 }}>Hủy</Button>
                                            <Button type="primary" htmlType="submit" onClick={onSave} style={{ width: '100%', height: 40 }}>
                                                {t(translations.user.save)}
                                            </Button>
                                        </div>
                                    )
                                }


                            </Footer>
                        </Col>
                    </Row>
                </Col>
            </div>
        </Spin>
    );
}

